import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import Logo from '../../components/common/Logo';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loading } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendingOtp, setResendingOtp] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm();

  const role = searchParams.get('role')?.toUpperCase() || 'BUYER';

  const roleConfig = {
    buyer: {
      title: 'Customer Portal',
      subtitle: 'Log in to track your orders & refunds',
      gradient: 'linear-gradient(135deg, var(--primary), var(--primary-dark))',
    },
    mediator: {
      title: 'Mediator Portal',
      subtitle: 'Manage your buyers and earnings',
      gradient: 'linear-gradient(135deg, var(--accent), #4545C8)',
    },
    admin: {
      title: 'Admin Dashboard',
      subtitle: 'Manage your platform',
      gradient: 'linear-gradient(135deg, #0F172A, #1E293B)',
    },
    seller: {
      title: 'Seller Portal',
      subtitle: 'Manage your products and sales',
      gradient: 'linear-gradient(135deg, #F59E0B, #D97706)',
    },
  };

  const currentConfig = roleConfig[role] || roleConfig.buyer;

  useEffect(() => {
    // Load saved credentials if remember me was checked
    const savedCredentials = localStorage.getItem(`remember_${role}`);
    if (savedCredentials) {
      const { email, password } = JSON.parse(savedCredentials);
      setValue('email', email);
      setValue('password', password);
      setRememberMe(true);
    }
  }, [role, setValue]);

    const onSubmit = async (data) => {
        try {
            const result = await login({ ...data, role });

            if (result.success) {
                if (rememberMe) {
                    localStorage.setItem(`remember_${role}`, JSON.stringify({
                        email: data.email,
                        password: data.password
                    }));
                } else {
                    localStorage.removeItem(`remember_${role}`);
                }

                toast.success(`Welcome back, ${result.user.username || result.user.email}!`);

                const userRole = result.user.role.toLowerCase();

                const redirectPath = {
                    admin: '/admin',
                    mediator: '/mediator',
                    buyer: '/buyer',
                    seller: '/seller',
                }[userRole] || '/buyer';

                navigate(redirectPath);

            }else {
                const errorMsg = result.error || "Login failed";

                if (errorMsg.toLowerCase().includes("verify your email")) {
                    setNeedsEmailVerification(true);
                    setVerificationEmail(data.email);

                    // Optional toast
                    toast.error(errorMsg);

                } else {
                    toast.error(errorMsg);
                }
            }

        } catch (error) {
            console.error('Login error:', error);
            toast.error(error.error || 'An unexpected error occurred');
        }
    };

  const handleForgotPassword = () => {
    navigate('/forgot-password', { state: { role } });
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleResendOtp = async () => {
    if (!verificationEmail) return;
    
    setResendingOtp(true);
    try {
      const response = await authAPI.resendOtp(verificationEmail, 'EMAIL_VERIFICATION');
      if (response.data.success) {
        toast.success('OTP has been resent to your email');
      } else {
        toast.error('Unable to resend OTP. Please try again later.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setResendingOtp(false);
    }
  };

  return (
    <div 
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: 'linear-gradient(150deg, #F0FDF4, #EEF2FF)'
      }}
    >
      <div 
        style={{
          width: '100%',
          maxWidth: '448px',
          borderRadius: '24px',
          padding: '32px',
          boxShadow: 'var(--shadow-lg)',
          border: `1px solid var(--border)`,
          background: 'var(--bg-card)'
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            <Logo />
          </div>
          <h2 
            className="outfit"
            style={{ 
              fontWeight: '700', 
              fontSize: '24px', 
              marginBottom: '8px',
              color: 'var(--text)'
            }}
          >
            {currentConfig.title}
          </h2>
          <p 
            style={{ 
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--text-muted)'
            }}
          >
            {currentConfig.subtitle}
          </p>
        </div>

        {/* Role Selector */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Login as</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
            {['BUYER', 'MEDIATOR'].map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => navigate(`/login?role=${r}`)}
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  border: `2px solid ${role === r ? 'var(--primary)' : 'var(--border)'}`,
                  background: role === r ? 'var(--primary-light)' : 'var(--bg-deep)',
                  color: role === r ? 'var(--primary-dark)' : 'var(--text-sub)',
                  transition: 'all 0.2s',
                  textTransform: 'capitalize',
                  fontWeight: '500',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (role !== r) {
                    e.currentTarget.style.borderColor = 'var(--text-muted)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (role !== r) {
                    e.currentTarget.style.borderColor = 'var(--border)';
                  }
                }}
              >
                {r === 'buyer' ? 'Customer' : r}
              </button>
            ))}
          </div>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Email or Username</label>
            <input
              {...register('email', {
                required: 'Email or username is required',
                pattern: {
                  value: /^([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}|[a-zA-Z0-9_]{3,})$/i,
                  message: 'Invalid email or username'
                }
              })}
              type="text"
              placeholder="name@example.com or username"
              style={{
                width: '100%',
                borderColor: errors.email ? 'var(--red)' : 'var(--border)'
              }}
            />
            {errors.email && (
              <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.email.message}</p>
            )}
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters'
                  }
                })}
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                style={{
                  width: '100%',
                  paddingRight: '40px',
                  borderColor: errors.password ? 'var(--red)' : 'var(--border)'
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer'
                }}
              >
                <Icon name={showPassword ? 'eye' : 'eye'} size={16} />
              </button>
            </div>
            {errors.password && (
              <p style={{ color: 'var(--red)', fontSize: '12px', marginTop: '4px' }}>{errors.password.message}</p>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: 'var(--text-sub)' }}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ accentColor: 'var(--primary)', width: '14px', height: '14px' }}
              />
              Remember me
            </label>
            <button
              type="button"
              onClick={handleForgotPassword}
              style={{
                fontSize: '14px',
                fontWeight: '700',
                transition: 'colors 0.2s',
                color: 'var(--accent)',
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Forgot?
            </button>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={loading}
            style={{ width: '100%', padding: '12px' }}
          >
            Sign In
          </Button>

          {/* Email Verification Notice */}
          {needsEmailVerification && (
            <div style={{
              padding: '16px',
              background: '#FEF3C7',
              border: '1px solid #F59E0B',
              borderRadius: '8px',
              marginTop: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Icon name="alert-circle" size={16} style={{ color: '#D97706' }} />
                <span style={{ fontWeight: '600', color: '#92400E', fontSize: '14px' }}>
                  Please verify your email first
                </span>
              </div>
              <p style={{ color: '#92400E', fontSize: '13px', margin: '0 0 12px 0' }}>
                We've sent a verification email to <strong>{verificationEmail}</strong>. 
                Please check your inbox and click the verification link.
              </p>
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                        // Optional: call resend API before navigating
                        handleResendOtp();

                        // Navigate to OTP screen
                        navigate('/verify-otp', {
                            state: {
                                email: verificationEmail,
                                role: role
                            }
                        });
                    }}
                    style={{
                        width: '100%',
                        background: '#F59E0B',
                        borderColor: '#F59E0B',
                        color: 'white'
                    }}
                >
                    Click to Verify
                </Button>
            </div>
          )}
        </form>

        {/* Register Link */}
        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
            No account?{' '}
            <button
              onClick={() => navigate('/register')}
              style={{
                fontWeight: '700',
                transition: 'colors 0.2s',
                color: 'var(--primary)',
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Sign up
            </button>
          </p>
        </div>

        {/* Back Button */}
        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <button
            onClick={handleBack}
            style={{
              fontSize: '14px',
              fontWeight: '500',
              transition: 'colors 0.2s',
              color: 'var(--text-muted)',
              background: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
